import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { toast } from "react-toastify";
import { Button, Col, Row, Spinner } from "reactstrap";
import GoBack from "../../components/GoBack";
import Loader from "../../components/layout/Loader";
import NoDataFound from "../../components/layout/NoDataFound";
import TableComponent from "../../components/layout/TableComponent";
import { formatDateUS, formatTime } from "../../utils/commonFn";
import { ADMIN_ROUTE, AI_RECRUITER_HEALTH } from "../../utils/constant";
import {
  AI_RECRUITER_DEAD_LETTERED_EMAILS,
  AI_RECRUITER_JOB_STATUS,
  AI_RETRY_DEAD_LETTERED_EMAIL,
} from "./gql";

type AiJobRunStatus = "running" | "success" | "failed" | null;

interface AiJobRun {
  status: AiJobRunStatus;
  startedAt?: string | null;
  finishedAt?: string | null;
  durationMs?: number | null;
  // outcome is a JSONObject scalar — arbitrary shape.
  outcome?: Record<string, unknown> | null;
  errorMessage?: string | null;
}

interface AiJob {
  jobName: string;
  lastRun?: AiJobRun | null;
}

interface AiGauges {
  emailQueuePending: number;
  emailDeadLetter: number;
  stuckInQueue: number;
}

interface AiRecruiterJobStatusData {
  aiRecruiterJobStatus: {
    jobs: AiJob[];
    gauges: AiGauges;
  };
}

interface AiDeadLetteredEmail {
  id: string;
  recipient?: string | null;
  emailType?: string | null;
  sourceId?: string | null;
  attemptCount: number;
  deadLetterReason?: string | null;
  deadLetteredAt?: string | null;
  createdAt: string;
}

interface AiDeadLetteredEmailsData {
  aiRecruiterDeadLetteredEmails: AiDeadLetteredEmail[];
}

const JOB_STATUS_HEADERS = [
  "Job",
  "Status",
  "Last Finished",
  "Duration",
  "Details",
];

const DEAD_LETTER_HEADERS = [
  "Recipient",
  "Email Type",
  "Reason",
  "Dead-Lettered At",
  "Attempts",
  "Action",
];

const formatDateTime = (value?: string | null): string => {
  if (!value) return "--";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "--";
  return `${formatDateUS(d)} ${formatTime(d)}`;
};

// Human-readable duration from milliseconds.
const formatDuration = (ms?: number | null): string => {
  if (ms === null || ms === undefined) return "--";
  if (ms < 1000) return `${ms} ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

// Pull a couple of trivial numeric counts out of the outcome JSON, if present.
const summarizeOutcome = (
  outcome?: Record<string, unknown> | null,
): string => {
  if (!outcome || typeof outcome !== "object") return "";
  const keys = ["processed", "sent", "queued", "skipped", "failed", "count"];
  const parts: string[] = [];
  for (const key of keys) {
    const val = outcome[key];
    if (typeof val === "number") parts.push(`${key}: ${val}`);
  }
  return parts.join(", ");
};

const StatusPill = ({ status }: { status: AiJobRunStatus }) => {
  let bg = "#9aa0a6"; // grey — never run / unknown
  let label = "Never run";
  if (status === "success") {
    bg = "#28a745";
    label = "Success";
  } else if (status === "failed") {
    bg = "#dc3545";
    label = "Failed";
  } else if (status === "running") {
    bg = "#eaa827";
    label = "Running";
  }
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        color: "#fff",
        backgroundColor: bg,
        fontSize: "12px",
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
};

const GaugeTile = ({
  label,
  value,
  alert,
  warn,
}: {
  label: string;
  value: number;
  alert?: boolean;
  warn?: boolean;
}) => {
  let color = "#212529";
  let border = "#e0e0e0";
  if (alert && value > 0) {
    color = "#dc3545";
    border = "#dc3545";
  } else if (warn && value > 0) {
    color = "#eaa827";
    border = "#eaa827";
  }
  return (
    <div
      className="users-card"
      style={{
        textAlign: "center",
        borderLeft: `4px solid ${border}`,
      }}
    >
      <h5 className="m-0" style={{ color: "#6c757d", fontSize: "14px" }}>
        {label}
      </h5>
      <h2 className="m-0" style={{ color, fontWeight: 700 }}>
        {value}
      </h2>
    </div>
  );
};

const AiRecruiterHealth = () => {
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const {
    data: statusData,
    loading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useQuery<AiRecruiterJobStatusData>(AI_RECRUITER_JOB_STATUS, {
    fetchPolicy: "cache-and-network",
  });

  const {
    data: deadLetterData,
    loading: deadLetterLoading,
    error: deadLetterError,
    refetch: refetchDeadLetters,
  } = useQuery<AiDeadLetteredEmailsData>(AI_RECRUITER_DEAD_LETTERED_EMAILS, {
    fetchPolicy: "cache-and-network",
  });

  const [retryDeadLetteredEmail] = useMutation(AI_RETRY_DEAD_LETTERED_EMAIL);

  const handleRefresh = () => {
    refetchStatus();
    refetchDeadLetters();
  };

  const handleRetry = async (id: string) => {
    setRetryingId(id);
    try {
      await retryDeadLetteredEmail({ variables: { id } });
      toast.success("Email re-queued for sending.");
      // Refetch both so the dead-letter list drops the row and the gauge count updates.
      await Promise.all([refetchDeadLetters(), refetchStatus()]);
    } catch (err) {
      toast.error(
        `Failed to retry email: ${(err as Error)?.message ?? "Unknown error"}`,
      );
    } finally {
      setRetryingId(null);
    }
  };

  const gauges = statusData?.aiRecruiterJobStatus?.gauges;
  const jobs = statusData?.aiRecruiterJobStatus?.jobs ?? [];
  const deadLetters = deadLetterData?.aiRecruiterDeadLetteredEmails ?? [];

  return (
    <div className="admin-tabs team-tabs">
      <GoBack url={ADMIN_ROUTE} />

      <div className="primaryHeaderSpacing primaryHeading d-flex justify-content-between align-items-center">
        <h5 className="m-0">{AI_RECRUITER_HEALTH}</h5>

        <Button
          type="button"
          onClick={handleRefresh}
          className="buttonGenericStyle filledButton"
          disabled={statusLoading || deadLetterLoading}
        >
          {statusLoading || deadLetterLoading ? (
            <Spinner size="sm" />
          ) : (
            "Refresh"
          )}
        </Button>
      </div>

      {/* Gauge tiles */}
      <Row className="mr-3">
        <Col xl="4" lg="4" md="6" xs="12">
          <GaugeTile
            label="Email Queue Pending"
            value={gauges?.emailQueuePending ?? 0}
          />
        </Col>
        <Col xl="4" lg="4" md="6" xs="12">
          <GaugeTile
            label="Dead-Lettered"
            value={gauges?.emailDeadLetter ?? 0}
            alert
          />
        </Col>
        <Col xl="4" lg="4" md="6" xs="12">
          <GaugeTile
            label="Stuck In Queue"
            value={gauges?.stuckInQueue ?? 0}
            warn
          />
        </Col>
      </Row>

      {statusError && (
        <div className="mr-3 mt-3">
          <div className="text-danger">
            Failed to load job status: {statusError.message}
          </div>
        </div>
      )}

      {/* Cron job-status table */}
      <div className="primaryHeaderSpacing primaryHeading mt-3">
        <h5 className="m-0">Cron Jobs</h5>
      </div>
      <div className="mr-3">
        <TableComponent tableHeader={JOB_STATUS_HEADERS}>
          <tbody>
            {statusLoading && !jobs.length && (
              <tr>
                <td colSpan={JOB_STATUS_HEADERS.length}>
                  <Loader loaderClass="sm-height " />
                </td>
              </tr>
            )}

            {!statusLoading || jobs.length ? (
              jobs.length ? (
                jobs.map((job) => {
                  const run = job.lastRun;
                  const summary = summarizeOutcome(run?.outcome);
                  return (
                    <tr key={job.jobName}>
                      <td>{job.jobName}</td>
                      <td>
                        <StatusPill status={run?.status ?? null} />
                      </td>
                      <td>{formatDateTime(run?.finishedAt)}</td>
                      <td>{formatDuration(run?.durationMs)}</td>
                      <td>
                        {run?.status === "failed" && run?.errorMessage ? (
                          <div
                            className="text-danger text-truncate max-800 max-200"
                            title={run.errorMessage}
                          >
                            {run.errorMessage}
                          </div>
                        ) : summary ? (
                          <div className="text-truncate max-800 max-200">
                            {summary}
                          </div>
                        ) : (
                          "--"
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={JOB_STATUS_HEADERS.length}>
                    <div className="for-position-multiselect">
                      <NoDataFound text="No cron jobs found." />
                    </div>
                  </td>
                </tr>
              )
            ) : null}
          </tbody>
        </TableComponent>
      </div>

      {/* Dead-letter table */}
      <div className="primaryHeaderSpacing primaryHeading mt-3">
        <h5 className="m-0">Dead-Lettered Emails</h5>
      </div>

      {deadLetterError && (
        <div className="mr-3">
          <div className="text-danger">
            Failed to load dead-lettered emails: {deadLetterError.message}
          </div>
        </div>
      )}

      <div className="mr-3">
        <TableComponent tableHeader={DEAD_LETTER_HEADERS}>
          <tbody>
            {deadLetterLoading && !deadLetters.length && (
              <tr>
                <td colSpan={DEAD_LETTER_HEADERS.length}>
                  <Loader loaderClass="sm-height " />
                </td>
              </tr>
            )}

            {!deadLetterLoading || deadLetters.length ? (
              deadLetters.length ? (
                deadLetters.map((email) => {
                  const inFlight = retryingId === email.id;
                  return (
                    <tr key={email.id}>
                      <td>{email.recipient ?? "--"}</td>
                      <td>{email.emailType ?? "--"}</td>
                      <td>
                        <div
                          className="text-truncate max-800 max-200"
                          title={email.deadLetterReason ?? ""}
                        >
                          {email.deadLetterReason ?? "--"}
                        </div>
                      </td>
                      <td>{formatDateTime(email.deadLetteredAt)}</td>
                      <td>{email.attemptCount}</td>
                      <td>
                        <Button
                          type="button"
                          size="sm"
                          className="buttonGenericStyle filledButton"
                          disabled={inFlight}
                          onClick={() => handleRetry(email.id)}
                        >
                          {inFlight ? <Spinner size="sm" /> : "Retry"}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={DEAD_LETTER_HEADERS.length}>
                    <div className="for-position-multiselect">
                      <NoDataFound text="No dead-lettered emails." />
                    </div>
                  </td>
                </tr>
              )
            ) : null}
          </tbody>
        </TableComponent>
      </div>
    </div>
  );
};

export default AiRecruiterHealth;
